import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, CalendarClock, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Switch,
} from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { listToText, textToList } from '../quests/quest-shared';
import {
  ADMIN_BOUNTY,
  ADMIN_BOUNTY_CATALOG,
  ADMIN_CREATE_BOUNTY,
  ADMIN_INVITE_TO_BOUNTY,
  ADMIN_ROTATE_BOUNTY_INVITE,
  ADMIN_SET_BOUNTY_STATUS,
  ADMIN_UNSCHEDULE_BOUNTY,
  ADMIN_UPDATE_BOUNTY,
  bountyInviteUrl,
  bountyPageUrl,
  periodWord,
  scheduleLabel,
  stepPanelPreviewUrl,
  type AdminBounty,
  kindNoun,
  type BountyCatalogEntry,
} from './bounty-shared';
import { EngineBountySettings, engineConfigToForm, engineConfigToInput } from './EngineBountySettings';
import { MemberPicker, type PickedMember } from './MemberPicker';
import { ScheduleBountyDialog } from './ScheduleBountyDialog';
import {
  bountyNameError,
  bountySaveErrorMessage,
  bountyTargetError,
  DEFAULT_STEP_BOUNTY_POINTS,
  memberCount,
  payoutLabel,
  publishMatchLine,
} from './bounty-name';
import { StepBountyForm, type BountyForm } from './StepBountyForm';
import { SeriesReachBackField, canReachBack, reachBackValue, seriesPublishLine } from './SeriesReachBack';
import { memberGateInt } from './member-gates';

type Form = BountyForm;

const SERIES_KEY = 'onboarding';
function toForm(bounty: AdminBounty | null, newKind: AdminBounty['kind'] = 'task'): Form {
  const overrides = bounty?.stepOverrides ?? {};
  const kind = bounty?.kind ?? newKind;
  // A new message or card item starts in the series, once, at no points, inbox and email on.
  const item = !bounty && kind !== 'task';
  return {
    kind,
    name: bounty?.name ?? '',
    slug: bounty?.slug ?? '',
    emoji: bounty?.emoji ?? '',
    stepCode: bounty?.stepCode ?? '',
    target: bounty?.target ? String(bounty.target) : '',
    pointsReward: String(bounty?.pointsReward ?? (item ? 0 : DEFAULT_STEP_BOUNTY_POINTS)),
    cadence: bounty?.cadence ?? 'once',
    inSeries: item || Boolean(bounty?.seriesKey),
    seriesDay: bounty?.seriesDay !== null && bounty?.seriesDay !== undefined ? String(bounty.seriesDay) : '',
    enrollment: bounty?.enrollment ?? 'everyone',
    channelNotify: bounty?.channelNotify ?? true,
    channelPin: bounty?.channelPin ?? !item,
    channelEmail: bounty?.channelEmail ?? false,
    // A new bounty or item emails as a line in the nightly notification email (Nick, 2026-10-13).
    channelDigest: bounty?.isInDailyEmail ?? true,
    intents: listToText(bounty?.audience?.intents),
    careerStages: listToText(bounty?.audience?.careerStages),
    countryCodes: listToText(bounty?.audience?.countryCodes),
    minAccountAgeDays: optionalNumberText(bounty?.exclusions?.minAccountAgeDays),
    requireTier2Activated: bounty?.exclusions?.requireTier2Activated === true,
    minReputationPoints: optionalNumberText(bounty?.exclusions?.minReputationPoints),
    profileCompleteness: bounty?.exclusions?.profileCompleteness ?? 'any',
    priority: String(bounty?.priority ?? 0),
    label: String(overrides.label ?? ''),
    why: String(overrides.why ?? ''),
    evidence: String(overrides.evidence ?? ''),
    cta: String(overrides.cta ?? ''),
    minReplies: bounty?.config?.minReplies ? String(bounty.config.minReplies) : '',
    showOnPoints: bounty?.showOnPoints ?? false,
    showOnBounties: bounty?.showOnBounties ?? true,
    notifyReminder: (bounty?.notifyReminder as Form['notifyReminder']) ?? null,
    emailReminder: (bounty?.emailReminder as Form['emailReminder']) ?? null,
    config: bounty?.systemKey ? engineConfigToForm(bounty.systemKey, bounty.config) : {},
  };
}

function optionalNumberText(value: number | null | undefined): string {
  return value ? String(value) : '';
}

/**
 * The bounty's exclusions with the form's member rules applied. Keys the form does not edit
 * (signedUpBefore and the max-count rules) are carried over as they were, so saving here
 * never wipes a rule set elsewhere.
 */
function exclusionsInput(form: Form, existing: AdminBounty['exclusions']) {
  return {
    signedUpBefore: existing?.signedUpBefore ?? null,
    maxVotesCast: existing?.maxVotesCast ?? null,
    maxCommentsWritten: existing?.maxCommentsWritten ?? null,
    maxPostsRead: existing?.maxPostsRead ?? null,
    minAccountAgeDays: memberGateInt(form.minAccountAgeDays, 'Minimum age'),
    requireTier2Activated: form.requireTier2Activated ? true : null,
    minReputationPoints: memberGateInt(form.minReputationPoints, 'Minimum reputation'),
    profileCompleteness: form.profileCompleteness === 'any' ? null : form.profileCompleteness,
  };
}

function toInput(
  form: Form,
  systemKey: string | null,
  sendTarget = true,
  existingExclusions: AdminBounty['exclusions'] = null
) {
  if (systemKey) {
    return {
      name: form.name,
      emoji: form.emoji.trim() || null,
      enrollment: form.enrollment,
      cadence: form.cadence,
      showOnPoints: form.showOnPoints,
      showOnBounties: form.showOnBounties,
      config: engineConfigToInput(systemKey, form.cadence, form.config),
    };
  }
  const overrides = Object.fromEntries(
    (['label', 'why', 'evidence', 'cta'] as const).filter((k) => form[k].trim()).map((k) => [k, form[k].trim()])
  );
  const minReplies = form.stepCode === 'hotThread' && form.minReplies.trim() ? Number(form.minReplies) : null;
  const target = form.target.trim();
  return {
    kind: form.kind,
    name: form.name,
    slug: form.slug,
    emoji: form.emoji.trim() || null,
    stepCode: form.stepCode,
    target: sendTarget && target ? Number(target) : null,
    pointsReward: Number(form.pointsReward) || 0,
    cadence: form.cadence,
    seriesKey: form.inSeries ? SERIES_KEY : null,
    seriesDay: form.inSeries ? Number(form.seriesDay) || 0 : null,
    enrollment: form.enrollment,
    channelNotify: form.channelNotify,
    channelPin: form.channelPin,
    channelEmail: form.channelEmail,
    isInDailyEmail: form.channelDigest,
    showOnPoints: form.showOnPoints,
    showOnBounties: form.showOnBounties,
    notifyReminder: form.notifyReminder,
    emailReminder: form.emailReminder,
    audience: {
      intents: textToList(form.intents),
      careerStages: textToList(form.careerStages),
      countryCodes: textToList(form.countryCodes),
    },
    exclusions: exclusionsInput(form, existingExclusions),
    priority: Number(form.priority) || 0,
    stepOverrides: Object.keys(overrides).length > 0 ? overrides : null,
    ...(form.stepCode === 'hotThread' ? { config: minReplies ? { minReplies } : null } : {}),
  };
}

function slugFrom(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * THE BOUNTY EDITOR (#4118). A bounty is one catalog step, so the step picker is the
 * heart of it: each step shows why it can or cannot be used the way this form is set up,
 * and the copy fields show the step's own words as their placeholders — leaving them empty
 * keeps the step's language. "Open its panel" shows the real panel the member gets.
 */
export default function AdminBountyEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const newKind = (['message', 'suggestions'] as const).find((kind) => kind === searchParams.get('kind')) ?? 'task';
  const { data, loading, refetch } = useQuery<{ adminBounty: AdminBounty | null }>(ADMIN_BOUNTY, {
    variables: { id },
    skip: isNew,
    fetchPolicy: 'cache-and-network',
  });
  const { data: catalogData } = useQuery<{ adminBountyCatalog: BountyCatalogEntry[] }>(ADMIN_BOUNTY_CATALOG);
  const [createBounty, { loading: creating }] = useMutation(ADMIN_CREATE_BOUNTY);
  const [updateBounty, { loading: saving }] = useMutation(ADMIN_UPDATE_BOUNTY);
  const [setStatus] = useMutation(ADMIN_SET_BOUNTY_STATUS);
  const [inviteMembers] = useMutation(ADMIN_INVITE_TO_BOUNTY);
  const [rotateInvite] = useMutation(ADMIN_ROTATE_BOUNTY_INVITE);

  const bounty = data?.adminBounty ?? null;
  const system = Boolean(bounty?.systemKey);
  const live = bounty?.status === 'live';
  const [form, setForm] = useState<Form>(() => toForm(null, newKind));
  const [statusTarget, setStatusTarget] = useState<AdminBounty['status'] | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [unschedule] = useMutation(ADMIN_UNSCHEDULE_BOUNTY);
  const [reachBack, setReachBack] = useState('');
  const [invitee, setInvitee] = useState<PickedMember | null>(null);

  useEffect(() => {
    if (bounty) setForm(toForm(bounty));
  }, [bounty]);

  // A scheduled draft goes live on the server; once its time has passed, check each minute so
  // the page shows it live instead of a banner that has gone out of date.
  const scheduledFor = bounty?.status === 'draft' ? bounty.scheduledFor : null;
  useEffect(() => {
    if (!scheduledFor) return;
    const handle = window.setInterval(() => {
      if (Date.now() >= new Date(scheduledFor).getTime()) void refetch();
    }, 60_000);
    return () => window.clearInterval(handle);
  }, [scheduledFor, refetch]);

  const catalog = useMemo(() => catalogData?.adminBountyCatalog ?? [], [catalogData]);
  const step = catalog.find((entry) => entry.code === form.stepCode) ?? null;
  const setField = <K extends keyof Form>(key: K, value: Form[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  /** The rule's verdict for this step, used the way the form is set up right now. */
  const verdict = (entry: BountyCatalogEntry): string | null => {
    if (form.cadence !== 'once') return entry.recurringVerdict;
    return form.inSeries ? entry.seriesVerdict : entry.onceVerdict;
  };

  const handleSave = async () => {
    const nameError = bountyNameError(form.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    // No bar means the count field is hidden. A leftover "0" must not block the save,
    // and must not be stored. Unknown step (catalog still loading) keeps the check.
    const sendTarget = step ? Boolean(step.bar) : true;
    if (sendTarget) {
      const targetError = bountyTargetError(form.target);
      if (targetError) {
        toast.error(targetError);
        return;
      }
    }
    try {
      const input = toInput(form, bounty?.systemKey ?? null, sendTarget, bounty?.exclusions ?? null);
      if (isNew) {
        const result = await createBounty({ variables: { input } });
        toast.success(form.kind === 'task' ? 'Bounty created as a draft.' : 'Series item created as a draft.');
        navigate(`/admin/bounties/${result.data.adminCreateBounty.id}`, { replace: true });
        return;
      }
      await updateBounty({ variables: { id, input } });
      toast.success('Saved.');
      void refetch();
    } catch (err) {
      toast.error(bountySaveErrorMessage(err, form.name));
    }
  };

  const emailsEveryone = Boolean(
    bounty && bounty.channelEmail && bounty.enrollment === 'everyone' && !bounty.seriesKey && bounty.cadence !== 'once'
  );

  const handleStatus = async () => {
    if (!statusTarget || !bounty) return;
    try {
      await setStatus({
        variables: {
          id: bounty.id,
          status: statusTarget,
          confirmEmailBlast: statusTarget === 'live' && emailsEveryone,
          reachBackDays: statusTarget === 'live' ? reachBackValue(reachBack) : null,
        },
      });
      toast.success(`Now ${statusTarget}.`);
      setStatusTarget(null);
      void refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to change status');
    }
  };

  const handleUnschedule = async () => {
    if (!bounty) return;
    try {
      await unschedule({ variables: { id: bounty.id } });
      toast.success('No longer scheduled. It stays a draft.');
      void refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unschedule');
    }
  };

  const handleInvite = async () => {
    if (!invitee || !bounty) return;
    try {
      const result = await inviteMembers({ variables: { id: bounty.id, userIds: [invitee.asfId] } });
      toast.success(result.data.adminInviteToBounty ? 'Invited.' : 'Already invited.');
      setInvitee(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to invite');
    }
  };

  const handleRotate = async () => {
    if (!bounty) return;
    const result = await rotateInvite({ variables: { id: bounty.id } });
    await navigator.clipboard.writeText(bountyInviteUrl(result.data.adminRotateBountyInvite));
    toast.success('New link copied. The old link no longer works.');
    void refetch();
  };

  if (!isNew && loading && !bounty) {
    return (
      <AdminLayout title="Bounty" description="Loading…">
        <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-gray-400" />
      </AdminLayout>
    );
  }
  if (!isNew && !bounty) {
    return (
      <AdminLayout title="Bounty" description="It may have been deleted.">
        <p className="text-sm text-gray-500">Bounty not found.</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={
        isNew
          ? form.kind === 'task'
            ? 'New bounty'
            : 'New series item'
          : `${bounty?.emoji ?? ''} ${bounty?.name}`.trim()
      }
      description={
        system
          ? 'Paid as it happens. Tune it here, or set it to draft to stop it paying.'
          : form.kind === 'task'
            ? 'One step, paid in points — in the step’s own words, opening the step’s own panel.'
            : 'Shows up in a new member’s first weeks on its day. Points are optional.'
      }
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/bounties">
              <ArrowLeft className="mr-2 h-4 w-4" /> All bounties
            </Link>
          </Button>
          {bounty && bounty.status === 'draft' ? (
            <Button variant="outline" onClick={() => setScheduling(true)} data-testid="bounty-schedule">
              <CalendarClock className="mr-2 h-4 w-4" /> {bounty.scheduledFor ? 'Reschedule' : 'Schedule'}
            </Button>
          ) : null}
          {bounty?.scheduledFor ? (
            <Button variant="outline" onClick={() => void handleUnschedule()} data-testid="bounty-unschedule">
              Unschedule
            </Button>
          ) : null}
          {bounty ? (
            <label className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm">
              <Switch
                checked={live}
                onCheckedChange={(on) => setStatusTarget(on ? 'live' : 'draft')}
                aria-label={live ? 'Take it to draft' : 'Put it live'}
                data-testid="bounty-live-switch"
              />
              {live ? 'Live' : bounty.status === 'archived' ? 'Archived' : 'Draft'}
            </label>
          ) : null}
          <Button onClick={() => void handleSave()} disabled={creating || saving} data-testid="bounty-save">
            {creating || saving ? 'Saving…' : isNew ? 'Create draft' : 'Save'}
          </Button>
        </div>
      }
    >
      {bounty?.scheduledFor ? (
        <p className="mb-4 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="bounty-scheduled-banner">
          Goes live {scheduleLabel(bounty.scheduledFor)}
          {bounty.scheduledReachBackDays
            ? `, also paying members who signed up in the ${bounty.scheduledReachBackDays} days before`
            : ''}
          . Until then it is a draft: it pays nothing and shows nowhere.
        </p>
      ) : null}
      {live && !system ? (
        <p className="mb-4 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600" data-testid="bounty-live-locks">
          {form.kind === 'task'
            ? 'Live: how often, enrollment, its step, its count and its address are locked so what members already earned stays decided. Switch it to draft to change them.'
            : 'Live: what it shows, its kind and its address are locked. Its day, words, channels and points can still change.'}
        </p>
      ) : null}
      {bounty?.scheduleError ? (
        <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="bounty-schedule-error">
          Its scheduled go-live failed: {bounty.scheduleError} Fix it, then schedule it again or Go live.
        </p>
      ) : null}
      <ScheduleBountyDialog
        bounty={scheduling ? bounty : null}
        emailsEveryone={emailsEveryone}
        onClose={() => setScheduling(false)}
        onScheduled={() => void refetch()}
      />
      <div className="grid max-w-5xl grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]" data-testid="bounty-editor">
        <div className="min-w-0 space-y-6">
          {system ? (
            <>
              <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_90px]">
                <div className="space-y-1">
                  <Label htmlFor="b-name">Name</Label>
                  <Input id="b-name" value={form.name} onChange={(e) => setField('name', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="b-emoji">Admin icon</Label>
                  <Input id="b-emoji" value={form.emoji} onChange={(e) => setField('emoji', e.target.value)} />
                  <p className="text-[11px] text-gray-500">Members always see 🎯</p>
                </div>
              </div>
              <EngineBountySettings
                systemKey={bounty!.systemKey!}
                cadence={form.cadence}
                enrollment={form.enrollment}
                config={form.config}
                onCadence={(value) => setField('cadence', value)}
                onEnrollment={(value) => setField('enrollment', value)}
                onConfig={(value) => setField('config', value)}
              />
              <label className="flex items-center gap-2 text-sm" data-testid="bounty-show-on-points">
                <Switch checked={form.showOnPoints} onCheckedChange={(checked) => setField('showOnPoints', checked)} />
                Show on /points as a way to earn
              </label>
              <label className="flex items-center gap-2 text-sm" data-testid="bounty-show-on-bounties">
                <Switch
                  checked={form.showOnBounties}
                  onCheckedChange={(checked) => setField('showOnBounties', checked)}
                />
                List on /bounties (off keeps it paying, unlisted)
              </label>
            </>
          ) : (
            <StepBountyForm
              form={form}
              setField={setField}
              catalog={catalog}
              step={step}
              verdict={verdict}
              live={live}
              isNew={isNew}
              slugFrom={slugFrom}
            />
          )}
        </div>

        <aside className="space-y-4">
          {bounty && !system ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="bounty-preview">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">What the member sees</p>
              <p className="mt-2 font-medium text-gray-900">
                {bounty.emoji ? `${bounty.emoji} ` : ''}
                {bounty.preview.label}
              </p>
              {/* A message or card item reads as its body (and a button), not as a task's why. */}
              <p className="mt-1 text-sm text-gray-600">
                {bounty.kind === 'task' ? bounty.preview.why : bounty.preview.evidence}
              </p>
              {bounty.kind === 'message' ? (
                <span className="mt-2 inline-block rounded-md bg-green-600 px-3 py-1 text-xs font-semibold text-white">
                  {bounty.preview.cta || 'Open it'}
                </span>
              ) : null}
              <p className="mt-2 text-xs text-gray-500">
                {payoutLabel(bounty.pointsReward)} · opens {bounty.preview.opens}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a href={stepPanelPreviewUrl(bounty.stepCode)} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-1 h-3 w-3" /> Open its panel
                  </a>
                </Button>
                {live ? (
                  <Button variant="outline" size="sm" asChild>
                    <a href={bountyPageUrl(bounty.slug)} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-1 h-3 w-3" /> Its page
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {bounty ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <p>
                Audience: {memberCount(bounty.audienceCount)} of {memberCount(bounty.audienceTotal)}
              </p>
              {bounty.kind === 'task' ? (
                <p>
                  {system
                    ? `Paid: ${bounty.paidCount.toLocaleString()} times since it went live, less any taken back`
                    : `Paid: ${bounty.paidCount.toLocaleString()} times`}
                </p>
              ) : (
                <p data-testid="bounty-item-reach">
                  Reached {bounty.deliveredCount.toLocaleString()} in the inbox · emailed{' '}
                  {bounty.emailedCount.toLocaleString()} · {bounty.kind === 'message' ? 'opened' : 'connected from'}{' '}
                  {bounty.clickedCount.toLocaleString()}
                </p>
              )}
              {bounty.eligibleSignupFrom ? (
                <p>
                  {bounty.kind === 'task' ? 'Pays' : 'Reaches'} members who signed up from{' '}
                  {new Date(bounty.eligibleSignupFrom).toLocaleString()}
                </p>
              ) : null}
            </div>
          ) : null}

          {bounty && bounty.enrollment !== 'everyone' ? (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4" data-testid="bounty-invite-panel">
              <p className="text-sm font-semibold text-gray-900">
                {bounty.enrollment === 'invite' ? 'Invite members' : 'Add members'}
              </p>
              <MemberPicker picked={invitee} onPick={setInvitee} testId="bounty-invite-search" />
              <Button size="sm" disabled={!invitee} onClick={() => void handleInvite()}>
                Invite
              </Button>
              <div className="border-t pt-3">
                <p className="text-xs text-gray-500">
                  The link is the invitation. Making a new one stops the old one working.
                </p>
                {bounty.inviteToken ? (
                  <p className="mt-1 break-all text-xs text-gray-700">{bountyInviteUrl(bounty.inviteToken)}</p>
                ) : null}
                <Button size="sm" variant="outline" className="mt-2" onClick={() => void handleRotate()}>
                  <Copy className="mr-1 h-3 w-3" /> {bounty.inviteToken ? 'New link' : 'Make a link'}
                </Button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <AlertDialog
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (open) return;
          setStatusTarget(null);
          setReachBack('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusTarget === 'live'
                ? `Publish this ${kindNoun(bounty?.kind)}?`
                : `Take this ${kindNoun(bounty?.kind)} to draft?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusTarget !== 'live'
                ? 'It stops paying and leaves every band and inbox. Points already paid stay paid.'
                : bounty?.seriesKey
                  ? seriesPublishLine(bounty, reachBackValue(reachBack))
                  : publishMatchLine(
                      bounty?.pointsReward ?? 0,
                      bounty?.audienceCount ?? 0,
                      emailsEveryone
                        ? ` It emails the recently active ones each ${bounty ? periodWord(bounty.cadence) : ''}.`
                        : ''
                    )}
            </AlertDialogDescription>
            {statusTarget === 'live' && bounty && canReachBack(bounty) ? (
              <SeriesReachBackField bounty={bounty} value={reachBack} onChange={setReachBack} />
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleStatus()}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
